import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import tzLookup from "tz-lookup";

// Components
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  Field,
  Input,
  Spinner,
  Textarea,
  Button,
  makeStyles,
  shorthands,
  tokens,
  useRestoreFocusSource,
  DrawerFooter,
  Link,
} from "@fluentui/react-components";
import { Dismiss24Regular, MyLocationRegular } from "@fluentui/react-icons";
import CountryCombobox from "../../../shared/components/CountryCombobox";
import { GenericComboBox } from "../../../shared/components/GenericComboBox";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";
import MapPointPicker from "../../../shared/components/MapPointPicker";

// Styles
import "../../../styles/animations.css";

// Business logic
import { useSelectedSitesContext } from "../context/SelectedSiteContext";
import { useSiteQuery } from "../hooks/useSiteQueries";
import { useCreateSite, useUpdateSite } from "../hooks/useSiteMutations";
import { siteSchema } from "@shared/schemas";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import { timezones } from "@shared/data";
import { getBrowserTimeDifference } from "../../../utils/time";
import { t } from "i18next";

const useStyles = makeStyles({
  root: {
    overflow: "visible",

    display: "flex",
    backgroundColor: "#fff",
    zIndex: "1000",
  },

  content: {
    flex: "1",
    padding: "16px",

    display: "grid",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gridRowGap: tokens.spacingVerticalXXL,
    gridAutoRows: "max-content",
  },

  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap("1rem"),
  },
});

const getTimezoneOffsetMinutes = (timezoneName) => {
  const match = timezoneName.match(/^\(UTC([+-])(\d{2}):(\d{2})\)/);
  if (!match) return 0;

  const sign = match[1] === "-" ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);

  return sign * (hours * 60 + minutes);
};

const compareTimezonesByOffset = (a, b) => {
  const offsetDiff = getTimezoneOffsetMinutes(a.name) - getTimezoneOffsetMinutes(b.name);
  if (offsetDiff !== 0) return offsetDiff;
  return a.name.localeCompare(b.name);
};

const getHoursDifferenceText = (timezoneKey) => {
  const diffHours = getBrowserTimeDifference(timezoneKey);
  if (diffHours === 0) return null;
  else if (diffHours > 0)
    return `${diffHours === 1 ? t("nounPhrases.hourAhead", { count: diffHours }) : t("nounPhrases.hoursAhead", { count: diffHours })}`;
  else
    return `${Math.abs(diffHours) === 1 ? t("nounPhrases.hourBehind", { count: Math.abs(diffHours) }) : t("nounPhrases.hoursBehind", { count: Math.abs(diffHours) })}`;
};

function SiteDrawer({ isOpen, setIsOpen, newSite }) {
  const restoreFocusSourceAttributes = useRestoreFocusSource();
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [editingSite, setEditingSite] = useState(null);
  const { value: selectedSites } = useSelectedSitesContext();
  const { notify } = useNotify();

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateSite = useItemValidation(siteSchema.$defs.site);

  // Errors
  const [errors, setErrors] = useState([]);
  const [isLocating, setIsLocating] = useState(false);

  const publicId = selectedSites?.[0]?.public_id ?? null;

  // 1. Query and mutations
  const { data, error, isError, isLoading, isSuccess } = useSiteQuery(publicId, isOpen);

  const {
    mutate: createSite,
    isPending: isCreating,
    error: errorCreating,
    reset: resetCreating,
  } = useCreateSite(
    "sites",
    (data) => {
      notify(t("item.messages.created", { name: data.name }), "success");
      handleClose();
    },
    null, // No need to handle error with the ErrorMessageBar conditional rendering
    false, // Extended logs
  );

  const {
    mutate: updateSite,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateSite(
    "sites",
    (data) => {
      notify(t("item.messages.saved", { name: data.name }), "success");
      handleClose();
    },
    null, // No need to handle error with the ErrorMessageBar conditional rendering
  );

  // 2. Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (errorCreating) {
      newErrors.push({ message: errorCreating?.message || t("generic.error"), type: "error", details: errorCreating });
    }
    if (errorUpdating) {
      newErrors.push({ message: errorUpdating?.message || t("generic.error"), type: "error", details: errorUpdating });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, errorCreating, errorUpdating, t]);

  useEffect(() => {
    if (!isOpen) return;

    if (newSite) {
      const initialSite = { id: 0, name: "" };
      setEditingSite(initialSite);
      // Don't validate immediately - let user start typing first
    } else if (data && isSuccess) {
      setEditingSite(data);
      setValidationErrors(validateSite(data));
    } else setEditingSite(null);
  }, [isOpen, data, newSite]);

  useEffect(() => {
    if (isOpen && isError) {
      notify(error.message, "error");
      handleClose();
    }
  }, [isOpen, isError]);

  // 3. Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["site", publicId] });
    setEditingSite(null);
    setValidationErrors({});
    setErrors([]);
    resetCreating();
    resetUpdating();
    setIsOpen(false);
  };

  const handleDrawHeaderTitleButtonOnClick = () => setIsOpen(false);

  const handleOpenChange = (_, data) => {
    console.log(data.type);
    if (data.type !== "backdropClick") setIsOpen(data.open);
  };

  const updateField = (field, value) => {
    const updated = { ...editingSite, [field]: value };
    setEditingSite(updated);
    setValidationErrors(validateSite(updated));
    //console.log(validationErrors, typeof validationErrors);
  };

  return (
    <div className={styles.root}>
      <Drawer
        {...restoreFocusSourceAttributes}
        type="overlay"
        position="end"
        separator
        size="medium"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={handleDrawHeaderTitleButtonOnClick}
              />
            }
          >
            {newSite ? t("site.new") : (editingSite?.name ?? "")}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!newSite && isLoading && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}
        <DrawerBody>
          <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("generic.code")}
              validationState={validationErrors.code ? "error" : undefined}
            >
              <Input
                id="code"
                maxLength="3"
                value={editingSite?.code ?? ""}
                style={{ width: "60px" }}
                onChange={(e) => updateField("code", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("generic.name")}
              validationState={validationErrors.name ? "error" : undefined}
              style={{ flex: 1, minWidth: 0 }}
              required
            >
              <Input
                id="name"
                maxLength="50"
                value={editingSite?.name ?? ""}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </Field>
          </div>
          <Field
            className={styles.field}
            label={t("generic.address")}
            validationState={validationErrors.address ? "error" : undefined}
          >
            <Textarea
              id="address"
              maxLength="255"
              value={editingSite?.address ?? ""}
              onChange={(e) => updateField("address", e.target.value)}
            />
          </Field>
          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("generic.zipcode")}
              validationState={validationErrors.zipcode ? "error" : undefined}
            >
              <Input
                id="zipcode"
                maxLength="10"
                value={editingSite?.zipcode ?? ""}
                style={{ width: "100px" }}
                onChange={(e) => updateField("zipcode", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("generic.city")}
              validationState={validationErrors.city ? "error" : undefined}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input
                id="city"
                maxLength="50"
                value={editingSite?.city ?? ""}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </Field>
          </div>
          <div style={{ display: "flex", gap: "10px " }}>
            <Field
              className={styles.field}
              validationState={validationErrors.state ? "error" : undefined}
              label={t("generic.state")}
              style={{ flex: "0.75" }}
            >
              <Input
                id="state"
                maxLength="50"
                value={editingSite?.state ?? ""}
                onChange={(e) => updateField("state", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("generic.country")}
              validationState={validationErrors.country_code ? "error" : undefined}
              style={{ flex: "1" }}
            >
              <CountryCombobox
                value={editingSite?.country_code ?? ""}
                onChange={(newCode) => updateField("country_code", newCode)}
              />
            </Field>
          </div>
          <Field
            className={styles.field}
            label={t("generic.timezone")}
            validationState={validationErrors.timezone ? "error" : undefined}
          >
            <GenericComboBox
              items={[...timezones].sort(compareTimezonesByOffset)}
              value={editingSite?.timezone ?? null}
              valueKey="key"
              displayKey="name"
              onOptionSelect={(newTimezone) => updateField("timezone", newTimezone)}
            />
            {editingSite?.latitude && editingSite?.longitude && (
              <Link
                disabled={!(editingSite?.latitude && editingSite?.longitude)}
                onClick={() => {
                  if (editingSite?.latitude && editingSite?.longitude) {
                    try {
                      const tz = tzLookup(editingSite.latitude, editingSite.longitude);
                      updateField("timezone", tz);
                    } catch (e) {
                      notify(t("item.messages.timezoneDetectionFailed"), "error");
                    }
                  }
                }}
              >
                {t("nounPhrases.detectAutomatically")}
              </Link>
            )}
            {editingSite?.timezone && getBrowserTimeDifference(editingSite.timezone) !== 0 && (
              <span style={{ fontStyle: "italic" }}>
                {`${t("nounPhrases.localTime")}: ${new Date().toLocaleTimeString(undefined, { timeZone: editingSite.timezone })} - ${getHoursDifferenceText(editingSite.timezone)}`}
              </span>
            )}
          </Field>
          <MapPointPicker
            style={{ marginTop: "20px", width: "100%", height: "300px" }}
            latitude={editingSite?.latitude}
            longitude={editingSite?.longitude}
            setCoordinates={(lat, lng) => {
              const updated = { ...editingSite, latitude: lat, longitude: lng };
              const tz = tzLookup(lat, lng);
              updated.timezone = tz;
              setEditingSite(updated);
              setValidationErrors(validateSite(updated));
            }} // --- IGNORE ---
            height={300}
          />
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              appearance="subtle"
              icon={isLocating ? <Spinner size="tiny" /> : <MyLocationRegular />}
              disabled={isLocating}
              onClick={() => {
                if (!navigator.geolocation) {
                  notify(
                    t("nounPhrases.geolocationUnsupported", {
                      defaultValue: "Geolocation is not supported by your browser",
                    }),
                    "error",
                  );
                  return;
                }
                setIsLocating(true);
                navigator.geolocation.getCurrentPosition(
                  ({ coords }) => {
                    const updated = { ...editingSite, latitude: coords.latitude, longitude: coords.longitude };
                    try {
                      updated.timezone = tzLookup(coords.latitude, coords.longitude);
                    } catch {}
                    setEditingSite(updated);
                    setValidationErrors(validateSite(updated));
                    setIsLocating(false);
                  },
                  (err) => {
                    notify(
                      `${t("nounPhrases.geolocationFailed", { defaultValue: "Unable to retrieve your location" })}: ${err.message}`,
                      "error",
                    );
                    setIsLocating(false);
                  },
                );
              }}
            />
            {editingSite?.latitude && editingSite?.longitude && (
              <span>{`${t("generic.latitude")}: ${editingSite?.latitude ?? ""} - ${t("generic.longitude")}: ${editingSite?.longitude ?? ""}`}</span>
            )}
          </div>
          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <DataValidationMessageBar validationErrors={validationErrors} />
          )}
        </DrawerBody>
        <DrawerFooter>
          <div className={styles.footerButtons}>
            <Button
              icon={isCreating || isUpdating ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={isCreating || isUpdating || Object.keys(validationErrors).length > 0}
              onClick={() => {
                // Validate one final time before submission
                const errors = validateSite(editingSite);
                if (Object.keys(errors).length > 0) {
                  setValidationErrors(errors);
                  return;
                }
                newSite ? createSite(editingSite, handleClose) : updateSite(editingSite, handleClose);
              }}
            >
              {newSite ? t("generic.create") : t("generic.update")}
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              {t("generic.cancel")}
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  );
}

export default SiteDrawer;
